import React, { useMemo } from 'react';

import type { ChipProps } from '@mui/material';
import { Chip as MuiChip } from '@mui/material';

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSparkles } from '@fortawesome/sharp-solid-svg-icons';
import { styled } from '@mui/styles';
import { alpha } from '@mui/system';

export interface ConfidenceIndicatorProps extends ChipProps {
  isCalculated?: boolean;
  valuePrefix?: string | React.ReactNode;
  units?: '%' | null;
  value: number | string;
}

type ColorName = 'primary' | 'success' | 'warning' | 'error';

export function ConfidenceIndicator({
  value,
  units = '%',
  isCalculated = false,
  valuePrefix,
  ...props
}: ConfidenceIndicatorProps) {
  const color = getColorFromValue(value);
  const displayValue = useMemo(() => getDisplayValue(value), [value]);
  const disabled = isCalculated || value === 1;

  return (
    <Chip
      color={color}
      label={
        (valuePrefix ? `${valuePrefix} ` : '') +
        displayValue +
        (units ? `${units}` : '')
      }
      variant="outlined"
      size="small"
      icon={disabled ? undefined : <FontAwesomeIcon icon={faSparkles} />}
      disabled={disabled}
      {...props}
    />
  );
}

const Chip = styled(MuiChip)(({ theme, ...props }) => {
  const colorname = (props.color as ColorName) || 'primary';
  const color = getThemeColorFromColor(colorname, theme.config);

  return {
    padding: '0.25rem 0.25rem 0.25rem 0.5rem',
    backgroundColor: alpha(color, 0.15),
    color,
    border: 'none',
    borderRadius: props.disabled ? '0.25rem' : '1rem',

    '& .MuiChip-icon': {
      fontSize: '1rem !important',
    },

    '&.Mui-disabled': {
      opacity: 1,
    },
  };
});

function getColorFromValue(value: number | string): ColorName {
  if (typeof value === 'string') {
    return 'primary';
  }

  if (value >= 0.9) {
    return 'success';
  }

  if (value >= 0.25) {
    return 'warning';
  }

  return 'error';
}

function getDisplayValue(value: number | string): string {
  if (typeof value === 'string') {
    return value;
  }

  return (value * 100).toFixed().toString();
}

function getThemeColorFromColor(colorname: ColorName, config: any): string {
  if (colorname === 'success') {
    return config.colors.textSuccessLight;
  }

  if (colorname === 'warning') {
    return config.colors.snackYellow;
  }

  if (colorname === 'error') {
    return config.colors.red[600];
  }

  return '#1976d2';
}
